import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { SectionFileDatasourceService } from 'file-datasource-service-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable, of } from 'rxjs';
import { UtilsService } from 'utils-service-lib';

@Component({
    selector: 'app-upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
    standalone: false
})
export class UploadFileComponent implements OnInit {
  private static MAX_LOCAL_FILE_SIZE_MB = 10; // should match limit of server
  private static MAX_LOCAL_FILE_SIZE_BYTES = 1024 * 1024 * UploadFileComponent.MAX_LOCAL_FILE_SIZE_MB;

  @Input() formGroup: UntypedFormGroup;
  @Input() workflowConfigId: string;
  @Input() title = 'File';
  @Input() tooltip = '';
  @Input() acceptableFileTypes: string[] = ['.*'];
  @Input() notifyUserOnInvalidFile = false;
  @Input() showInfoIcon = true;
  @Input() getFileInfo: () => Observable<string> = this.noInfo.bind(this);
  @Input() fileValid = false;
  @Output() fileNameChange = new EventEmitter<string>();

  templateInfo = {
    message: undefined,
    enabled: true,
    reset: function () {
      this.message = 'Click to retrieve information.';
      this.enabled = true;
    },
  };

  disableTemplateTooltip = false;

  constructor(private sectionService: SectionFileDatasourceService,
              private messageNotificationService: MessageNotificationService
  ) {
    this.templateInfo.reset();
  }

  ngOnInit(): void {
    this.fileNameChange.emit(this.formGroup.get('sourceValue').value);
  }

  // files are uploaded on save of the template rather than instantly (incase they don't save their changes)
  queueFileUpload(event: any): void {
    const file: File = event.files.item(0);

    const allowedMimeType = this.acceptableFileTypes
      .map(value => value?.split('.').pop())
      .map(fileType => UtilsService.extensionToMime(fileType))
      .find(mimeType => file.type === mimeType);
    if (!allowedMimeType) {
      const fileNameExt = file.name.split('.').pop();
      this.messageNotificationService.error(`File type .${fileNameExt} is not allowed.`);
      return;
    }

    if (file.size > UploadFileComponent.MAX_LOCAL_FILE_SIZE_BYTES) {
      this.messageNotificationService.error(`File size exceeds ${UploadFileComponent.MAX_LOCAL_FILE_SIZE_MB}MB limit.`);
      return;
    }

    this.formGroup.get('sourceValue').setValue(file.name);
    this.formGroup.get('file').setValue(file);
    this.formGroup.markAsDirty();

    this.templateInfo.reset();
    this.fileNameChange.emit(file.name);
  }

  clearFile(): void {
    this.formGroup.get('file').setValue(null);
    this.formGroup.get('sourceValue').setValue(null);
    this.formGroup.markAsDirty();
    this.fileNameChange.emit(null);
  }

  get existingFilename(): string {
    return this.formGroup.get('sourceValue').value;
  }

  get proposedFilename(): string {
    return this.formGroup.get('file').value?.name;
  }

  downloadFile(): void {
    this.sectionService
      .downloadSectionFile(this.workflowConfigId, this.formGroup.get('id').value, this.formGroup.get('sourceValue').value)
      .subscribe(
        (f) => {
          this.messageNotificationService.info('Downloading file...');
          UtilsService.downloadFile(f, this.formGroup.get('sourceValue').value);
        },
        (error) => {
          this.messageNotificationService.error('Error downloading file.', error);
        }
      );
  }

  getInfo(): void {
    if (!this.templateInfo.enabled) {
      return;
    }

    this.templateInfo.message = 'Retrieving information.';
    this.templateInfo.enabled = false;

    this.getFileInfo().subscribe({
      next: (info) => {
        this.templateInfo.message = info;
      },
      error: (err) => {
        console.error(err);
        this.templateInfo.message = `<p>An error occurred.</p>`;
      },
    });
  }

  private noInfo(): Observable<string> {
    return of('No information available.');
  }
}
